import { useEffect, useMemo, useState } from "react"
import { Edit, Plus, RefreshCw, Search, Trash2 } from "lucide-react"
import useCurrentUser from "@/hooks/use-current-user"
import { useGetCouncilPage } from "@/hooks/api/use-get-council-page"
import {
  useConvertMeterInfoHardwareToGlobal,
  useCreateMeterInfoBrand,
  useCreateMeterInfoHardware,
  useDeactivateMeterInfoBrand,
  useDeactivateMeterInfoHardware,
  useGetMeterInfoSnapshot,
  useMeterInfoBrands,
  useMeterInfoHardware,
  useUpdateMeterInfoBrand,
  useUpdateMeterInfoHardware,
} from "@/hooks/api"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { toast } from "@/components/ui/sonner"
import type {
  CouncilDTO,
  MeterCatalogItemDTO,
  MeterInfoBrandRequest,
  MeterInfoHardwareRequest,
} from "@/gen/api"
import {
  ListBrandsScopeEnum,
  ListHardwareScopeEnum,
  MeterInfoBrandRequestScopeEnum,
  MeterInfoHardwareRequestScopeEnum,
  MeterInfoHardwareRequestTypeEnum,
  MeterInfoHardwareRequestVariantEnum,
} from "@/gen/api"

type ViewMode = "GLOBAL" | "COUNCIL"
type MeterInfoTab = "hardware" | "brands"
type DialogType = "brand" | "hardware" | null

const emptyBrandForm = { code: "", name: "" }
const emptyHardwareForm = {
  model: "",
  name: "",
  brandAdminPortalId: "",
  type: "UNKNOWN",
  variant: "UNKNOWN",
}

const itemMatchesSearch = (item: MeterCatalogItemDTO, search: string) => {
  const value = search.trim().toLowerCase()
  if (!value) return true
  return [item.code, item.name, item.model, item.brandCode, item.type, item.variant, item.scope]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(value))
}

const statusBadge = (item: MeterCatalogItemDTO) => {
  if (item.active === false || item.deletedAt) return <Badge variant="destructive">Deleted</Badge>
  return <Badge className="bg-green-500">Active</Badge>
}

const scopeBadge = (scope?: string) =>
  scope === "COUNCIL" ? (
    <Badge variant="secondary">Council</Badge>
  ) : (
    <Badge variant="outline">Global</Badge>
  )

export default function MeterInfoPage() {
  const { hasRole } = useCurrentUser()
  const [viewMode, setViewMode] = useState<ViewMode>("GLOBAL")
  const [selectedCouncilCode, setSelectedCouncilCode] = useState("")
  const [activeTab, setActiveTab] = useState<MeterInfoTab>("hardware")
  const [search, setSearch] = useState("")
  const [dialog, setDialog] = useState<DialogType>(null)
  const [editingBrand, setEditingBrand] = useState<MeterCatalogItemDTO | null>(null)
  const [editingHardware, setEditingHardware] = useState<MeterCatalogItemDTO | null>(null)
  const [convertDialogOpen, setConvertDialogOpen] = useState(false)
  const [convertSourceGlobalId, setConvertSourceGlobalId] = useState("")
  const [brandForm, setBrandForm] = useState(emptyBrandForm)
  const [hardwareForm, setHardwareForm] = useState(emptyHardwareForm)

  const councilsPage = useGetCouncilPage(undefined)
  const councils = useMemo(
    () => councilsPage.data?.pages.flatMap((page) => page.content ?? []) ?? [],
    [councilsPage.data],
  )
  const selectedCouncil = councils.find((council) => council.code === selectedCouncilCode)
  const isCouncilView = viewMode === "COUNCIL"
  const councilCode = isCouncilView ? selectedCouncilCode : undefined

  useEffect(() => {
    if (viewMode === "COUNCIL" && !selectedCouncilCode && councils[0]?.code) {
      setSelectedCouncilCode(councils[0].code)
    }
  }, [councils, selectedCouncilCode, viewMode])

  const scope = isCouncilView ? undefined : ListBrandsScopeEnum.Global
  const hardwareScope = isCouncilView ? undefined : ListHardwareScopeEnum.Global
  const { data: snapshot } = useGetMeterInfoSnapshot(councilCode ?? "")
  const brandsQuery = useMeterInfoBrands(scope, councilCode)
  const hardwareQuery = useMeterInfoHardware(hardwareScope, councilCode)
  const globalHardwareQuery = useMeterInfoHardware(ListHardwareScopeEnum.Global, undefined)

  const createBrand = useCreateMeterInfoBrand()
  const updateBrand = useUpdateMeterInfoBrand()
  const deactivateBrand = useDeactivateMeterInfoBrand()
  const createHardware = useCreateMeterInfoHardware()
  const updateHardware = useUpdateMeterInfoHardware()
  const convertHardwareToGlobal = useConvertMeterInfoHardwareToGlobal()
  const deactivateHardware = useDeactivateMeterInfoHardware()

  const brands = useMemo(
    () => (brandsQuery.data ?? []).filter((item) => itemMatchesSearch(item, search)),
    [brandsQuery.data, search],
  )
  const hardware = useMemo(
    () => (hardwareQuery.data ?? []).filter((item) => itemMatchesSearch(item, search)),
    [hardwareQuery.data, search],
  )
  const globalHardwareOptions = useMemo(
    () =>
      (globalHardwareQuery.data ?? []).filter(
        (item) => item.adminPortalId && item.active !== false && !item.deletedAt,
      ),
    [globalHardwareQuery.data],
  )
  const brandOptions = useMemo(() => {
    const byId = new Map<number, MeterCatalogItemDTO>()
    for (const item of snapshot?.brands ?? brandsQuery.data ?? []) {
      if (!item.adminPortalId || item.active === false || item.deletedAt) continue
      byId.set(item.adminPortalId, item)
    }
    return [...byId.values()]
  }, [snapshot?.brands, brandsQuery.data])
  const openBrandDialog = (item?: MeterCatalogItemDTO) => {
    setEditingBrand(item ?? null)
    setBrandForm(item ? { code: item.code ?? "", name: item.name ?? "" } : emptyBrandForm)
    setDialog("brand")
  }

  const openHardwareDialog = (item?: MeterCatalogItemDTO) => {
    setEditingHardware(item ?? null)
    setHardwareForm(
      item
        ? {
            model: item.model ?? item.name ?? "",
            name: item.name ?? "",
            brandAdminPortalId: item.brandAdminPortalId ? String(item.brandAdminPortalId) : "",
            type: item.type ?? "UNKNOWN",
            variant: item.variant ?? "UNKNOWN",
          }
        : emptyHardwareForm,
    )
    setDialog("hardware")
  }

  const brandRequest = (): MeterInfoBrandRequest => ({
    scope: isCouncilView
      ? MeterInfoBrandRequestScopeEnum.Council
      : MeterInfoBrandRequestScopeEnum.Global,
    councilCode,
    code: brandForm.code.trim(),
    name: brandForm.name.trim() || undefined,
  })

  const hardwareRequest = (): MeterInfoHardwareRequest =>
    ({
      scope: isCouncilView
        ? MeterInfoHardwareRequestScopeEnum.Council
        : MeterInfoHardwareRequestScopeEnum.Global,
      councilCode,
      model: hardwareForm.model.trim(),
      name: hardwareForm.name.trim() || undefined,
      brandAdminPortalId: Number(hardwareForm.brandAdminPortalId),
      type: hardwareForm.type as MeterInfoHardwareRequestTypeEnum,
      variant: hardwareForm.variant as MeterInfoHardwareRequestVariantEnum,
    }) as MeterInfoHardwareRequest

  const saveBrand = async () => {
    if (!brandForm.code.trim()) return
    try {
      if (editingBrand?.adminPortalId)
        await updateBrand.mutateAsync({ id: editingBrand.adminPortalId, request: brandRequest() })
      else await createBrand.mutateAsync(brandRequest())
      setDialog(null)
      toast.success(editingBrand ? "Brand updated" : "Brand created")
    } catch {
      toast.error("Failed to save brand")
    }
  }

  const saveHardware = async () => {
    if (!hardwareForm.model.trim() || !hardwareForm.brandAdminPortalId) return
    try {
      if (editingHardware?.adminPortalId)
        await updateHardware.mutateAsync({
          id: editingHardware.adminPortalId,
          request: hardwareRequest(),
        })
      else await createHardware.mutateAsync(hardwareRequest())
      setDialog(null)
      toast.success(editingHardware ? "Hardware updated" : "Hardware created")
    } catch {
      toast.error("Failed to save hardware")
    }
  }

  const convertSelectedHardwareToGlobal = async () => {
    if (!editingHardware?.adminPortalId || !convertSourceGlobalId) return
    try {
      await convertHardwareToGlobal.mutateAsync({
        id: editingHardware.adminPortalId,
        request: { sourceGlobalHardwareId: Number(convertSourceGlobalId) },
      })
      setConvertDialogOpen(false)
      setDialog(null)
      setConvertSourceGlobalId("")
      toast.success("Hardware converted to global")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to convert hardware")
    }
  }

  if (!hasRole("super_admin")) {
    return (
      <div className="flex h-96 items-center justify-center text-muted-foreground">
        You do not have permission to access this page.
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-7rem)] min-h-0 flex-col gap-6 overflow-hidden">
      <Header />
      <ScopeBar
        viewMode={viewMode}
        councils={councils}
        selectedCouncilCode={selectedCouncilCode}
        onViewModeChange={setViewMode}
        onCouncilChange={setSelectedCouncilCode}
        onRefresh={() => {
          brandsQuery.refetch()
          hardwareQuery.refetch()
        }}
      />
      {isCouncilView && selectedCouncil && <CouncilBanner council={selectedCouncil} />}

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as MeterInfoTab)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <TabsList className="grid h-auto w-full grid-cols-2 lg:w-auto">
            <TabsTrigger value="hardware">Hardware</TabsTrigger>
            <TabsTrigger value="brands">Brands</TabsTrigger>
          </TabsList>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="pl-8 sm:w-72"
                placeholder="Search visible data..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {activeTab === "brands" && (
              <Button onClick={() => openBrandDialog()}>
                <Plus className="mr-2 size-4" />
                Add Brand
              </Button>
            )}
            {activeTab === "hardware" && (
              <Button onClick={() => openHardwareDialog()}>
                <Plus className="mr-2 size-4" />
                Add Hardware
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="hardware" className="min-h-0 flex-1">
          <HardwareTable
            items={hardware}
            onEdit={openHardwareDialog}
            onDeactivate={(id) => deactivateHardware.mutateAsync(id)}
          />
        </TabsContent>
        <TabsContent value="brands" className="min-h-0 flex-1">
          <BrandTable
            items={brands}
            onEdit={openBrandDialog}
            onDeactivate={(id) => deactivateBrand.mutateAsync(id)}
          />
        </TabsContent>
      </Tabs>

      <BrandDialog
        open={dialog === "brand"}
        editing={Boolean(editingBrand)}
        form={brandForm}
        viewMode={viewMode}
        council={selectedCouncil}
        isSaving={createBrand.isPending || updateBrand.isPending}
        onOpenChange={(open) => !open && setDialog(null)}
        onFormChange={setBrandForm}
        onSave={saveBrand}
      />
      <HardwareDialog
        open={dialog === "hardware"}
        editing={Boolean(editingHardware)}
        form={hardwareForm}
        viewMode={viewMode}
        council={selectedCouncil}
        brands={brandOptions}
        isSaving={createHardware.isPending || updateHardware.isPending}
        canConvertToGlobal={
          isCouncilView && editingHardware?.scope === "COUNCIL" && Boolean(editingHardware?.adminPortalId)
        }
        onOpenChange={(open) => !open && setDialog(null)}
        onFormChange={setHardwareForm}
        onSave={saveHardware}
        onConvertToGlobal={() => setConvertDialogOpen(true)}
      />
      <ConvertHardwareToGlobalDialog
        open={convertDialogOpen}
        hardware={editingHardware}
        globalHardware={globalHardwareOptions}
        selectedGlobalId={convertSourceGlobalId}
        isSaving={convertHardwareToGlobal.isPending}
        onOpenChange={(open) => {
          setConvertDialogOpen(open)
          if (!open) setConvertSourceGlobalId("")
        }}
        onSelectedGlobalIdChange={setConvertSourceGlobalId}
        onConfirm={convertSelectedHardwareToGlobal}
      />
    </div>
  )
}

function Header() {
  return (
    <div>
      <h1 className="text-3xl font-bold">Meter Info</h1>
      <p className="text-muted-foreground">Manage global defaults and council overrides.</p>
    </div>
  )
}

function ScopeBar({
  viewMode,
  councils,
  selectedCouncilCode,
  onViewModeChange,
  onCouncilChange,
  onRefresh,
}: {
  viewMode: ViewMode
  councils: CouncilDTO[]
  selectedCouncilCode: string
  onViewModeChange: (value: ViewMode) => void
  onCouncilChange: (value: string) => void
  onRefresh: () => void
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-4 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={viewMode === "GLOBAL" ? "default" : "outline"}
            onClick={() => onViewModeChange("GLOBAL")}
          >
            Global Defaults
          </Button>
          <Button
            variant={viewMode === "COUNCIL" ? "default" : "outline"}
            onClick={() => onViewModeChange("COUNCIL")}
          >
            Council Catalog
          </Button>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {viewMode === "COUNCIL" && (
            <Select value={selectedCouncilCode} onValueChange={onCouncilChange}>
              <SelectTrigger className="sm:w-72">
                <SelectValue placeholder="Select council" />
              </SelectTrigger>
              <SelectContent>
                {councils
                  .filter((council) => council.code)
                  .map((council) => (
                    <SelectItem key={council.code} value={council.code ?? ""}>
                      {council.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={onRefresh}>
            <RefreshCw className="mr-2 size-4" />
            Refresh
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function CouncilBanner({ council }: { council: CouncilDTO }) {
  return (
    <div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm">
      <span className="font-medium">{council.name}</span>
      <span className="ml-2 text-muted-foreground">{council.code}</span>
      <p className="mt-1 text-muted-foreground">
        Shows catalog rows stored in the admin portal for this council. Tenant systems apply these
        rows during meter catalog sync; this is not a live read from the tenant database.
      </p>
    </div>
  )
}

function BrandTable({
  items,
  readOnly,
  onEdit,
  onDeactivate,
}: {
  items: MeterCatalogItemDTO[]
  readOnly?: boolean
  onEdit?: (item: MeterCatalogItemDTO) => void
  onDeactivate?: (id: number) => void
}) {
  return (
    <Card
      className={
        readOnly
          ? "flex h-full min-h-0 flex-col border-0 shadow-none"
          : "flex h-full min-h-0 flex-col"
      }
    >
      {!readOnly && (
        <CardHeader>
          <CardTitle>Brands</CardTitle>
        </CardHeader>
      )}
      <CardContent className={readOnly ? "min-h-0 flex-1 p-0" : "min-h-0 flex-1"}>
        <div className="h-full min-h-0 overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Revision</TableHead>
                {!readOnly && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={`${item.scope}-${item.adminPortalId}-brand`}>
                  <TableCell className="font-mono text-sm">{item.code ?? "-"}</TableCell>
                  <TableCell className="font-medium">{item.name ?? "-"}</TableCell>
                  <TableCell>{scopeBadge(item.scope)}</TableCell>
                  <TableCell>{statusBadge(item)}</TableCell>
                  <TableCell>{item.revision ?? "-"}</TableCell>
                  {!readOnly && (
                    <TableCell className="text-right space-x-2">
                      <Button size="sm" variant="outline" onClick={() => onEdit?.(item)}>
                        <Edit className="size-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={!item.adminPortalId || item.active === false}
                        onClick={() => item.adminPortalId && onDeactivate?.(item.adminPortalId)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={readOnly ? 5 : 6}
                    className="py-8 text-center text-muted-foreground"
                  >
                    No brands found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

function HardwareTable({
  items,
  readOnly,
  onEdit,
  onDeactivate,
}: {
  items: MeterCatalogItemDTO[]
  readOnly?: boolean
  onEdit?: (item: MeterCatalogItemDTO) => void
  onDeactivate?: (id: number) => void
}) {
  return (
    <Card
      className={
        readOnly
          ? "flex h-full min-h-0 flex-col border-0 shadow-none"
          : "flex h-full min-h-0 flex-col"
      }
    >
      {!readOnly && (
        <CardHeader>
          <CardTitle>Hardware</CardTitle>
        </CardHeader>
      )}
      <CardContent className={readOnly ? "min-h-0 flex-1 p-0" : "min-h-0 flex-1"}>
        <div className="h-full min-h-0 overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Model</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Variant</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Changed</TableHead>
                {!readOnly && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={`${item.scope}-${item.adminPortalId}-hardware`}>
                  <TableCell className="font-medium">{item.model ?? item.name ?? "-"}</TableCell>
                  <TableCell>{item.name ?? "-"}</TableCell>
                  <TableCell>{item.brandCode ?? "-"}</TableCell>
                  <TableCell>{item.type ?? "-"}</TableCell>
                  <TableCell>{item.variant ?? "-"}</TableCell>
                  <TableCell>{scopeBadge(item.scope)}</TableCell>
                  <TableCell>{statusBadge(item)}</TableCell>
                  <TableCell>
                    {item.changedAt ? new Date(item.changedAt).toLocaleString() : "-"}
                  </TableCell>
                  {!readOnly && (
                    <TableCell className="text-right space-x-2">
                      <Button size="sm" variant="outline" onClick={() => onEdit?.(item)}>
                        <Edit className="size-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={!item.adminPortalId || item.active === false}
                        onClick={() => item.adminPortalId && onDeactivate?.(item.adminPortalId)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={readOnly ? 8 : 9}
                    className="py-8 text-center text-muted-foreground"
                  >
                    No hardware found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

function BrandDialog({
  open,
  editing,
  form,
  viewMode,
  council,
  isSaving,
  onOpenChange,
  onFormChange,
  onSave,
}: {
  open: boolean
  editing: boolean
  form: typeof emptyBrandForm
  viewMode: ViewMode
  council?: CouncilDTO
  isSaving: boolean
  onOpenChange: (open: boolean) => void
  onFormChange: (form: typeof emptyBrandForm) => void
  onSave: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Brand" : "Add Brand"}</DialogTitle>
          <DialogDescription>
            {viewMode === "COUNCIL"
              ? `Council override for ${council?.name ?? "selected council"}`
              : "Global default brand"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Brand code</Label>
            <Input
              value={form.code}
              onChange={(event) => onFormChange({ ...form, code: event.target.value })}
              placeholder="itron"
            />
          </div>
          <div className="space-y-2">
            <Label>Brand name</Label>
            <Input
              value={form.name}
              onChange={(event) => onFormChange({ ...form, name: event.target.value })}
              placeholder="Itron"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!form.code.trim() || isSaving} onClick={onSave}>
            {isSaving ? "Saving..." : "Save Brand"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function HardwareDialog({
  open,
  editing,
  form,
  viewMode,
  council,
  brands,
  isSaving,
  canConvertToGlobal,
  onOpenChange,
  onFormChange,
  onSave,
  onConvertToGlobal,
}: {
  open: boolean
  editing: boolean
  form: typeof emptyHardwareForm
  viewMode: ViewMode
  council?: CouncilDTO
  brands: MeterCatalogItemDTO[]
  isSaving: boolean
  canConvertToGlobal?: boolean
  onOpenChange: (open: boolean) => void
  onFormChange: (form: typeof emptyHardwareForm) => void
  onSave: () => void
  onConvertToGlobal?: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Hardware" : "Add Hardware"}</DialogTitle>
          <DialogDescription>
            {viewMode === "COUNCIL"
              ? `Council override for ${council?.name ?? "selected council"}`
              : "Global default hardware"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Model</Label>
            <Input
              value={form.model}
              onChange={(event) => onFormChange({ ...form, model: event.target.value })}
              placeholder="Model X"
            />
          </div>
          <div className="space-y-2">
            <Label>Display name</Label>
            <Input
              value={form.name}
              onChange={(event) => onFormChange({ ...form, name: event.target.value })}
              placeholder="Optional label"
            />
          </div>
          <div className="space-y-2">
            <Label>Brand</Label>
            <Select
              value={form.brandAdminPortalId}
              onValueChange={(brandAdminPortalId) => onFormChange({ ...form, brandAdminPortalId })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select brand" />
              </SelectTrigger>
              <SelectContent>
                {brands.map((brand) => (
                  <SelectItem key={brand.adminPortalId} value={String(brand.adminPortalId)}>
                    {brand.name || brand.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label="Type"
              value={form.type}
              values={["SMART_METER", "DATA_LOGGER", "UNKNOWN"]}
              onChange={(type) => onFormChange({ ...form, type })}
            />
            <SelectField
              label="Variant"
              value={form.variant}
              values={["WATER", "GAS", "ELECTRICITY", "UNKNOWN"]}
              onChange={(variant) => onFormChange({ ...form, variant })}
            />
          </div>
        </div>
        <DialogFooter>
          {canConvertToGlobal && (
            <Button type="button" variant="secondary" onClick={onConvertToGlobal}>
              Convert to Global
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!form.model.trim() || !form.brandAdminPortalId || isSaving}
            onClick={onSave}
          >
            {isSaving ? "Saving..." : "Save Hardware"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ConvertHardwareToGlobalDialog({
  open,
  hardware,
  globalHardware,
  selectedGlobalId,
  isSaving,
  onOpenChange,
  onSelectedGlobalIdChange,
  onConfirm,
}: {
  open: boolean
  hardware: MeterCatalogItemDTO | null
  globalHardware: MeterCatalogItemDTO[]
  selectedGlobalId: string
  isSaving: boolean
  onOpenChange: (open: boolean) => void
  onSelectedGlobalIdChange: (id: string) => void
  onConfirm: () => void
}) {
  const selected = globalHardware.find((item) => String(item.adminPortalId) === selectedGlobalId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Convert Local Hardware to Global</DialogTitle>
          <DialogDescription>
            This will update the current local hardware row to use the selected global definition
            and change its scope to Global.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <HardwareSummary title="Current local hardware" item={hardware} />
          <div className="space-y-2">
            <Label>Global hardware definition</Label>
            <Select value={selectedGlobalId} onValueChange={onSelectedGlobalIdChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select global hardware" />
              </SelectTrigger>
              <SelectContent>
                {globalHardware.map((item) => (
                  <SelectItem key={item.adminPortalId} value={String(item.adminPortalId)}>
                    {hardwareOptionLabel(item)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selected && <HardwareSummary title="Selected global definition" item={selected} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!selectedGlobalId || isSaving} onClick={onConfirm}>
            {isSaving ? "Converting..." : "Convert to Global"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function hardwareOptionLabel(item: MeterCatalogItemDTO) {
  return [item.model ?? item.name, item.brandCode, item.type, item.variant]
    .filter(Boolean)
    .join(" · ")
}

function HardwareSummary({ title, item }: { title: string; item: MeterCatalogItemDTO | null }) {
  return (
    <div className="rounded-md border p-3 text-sm">
      <div className="mb-2 font-medium">{title}</div>
      <div className="grid gap-1 text-muted-foreground sm:grid-cols-2">
        <div>Model: {item?.model ?? item?.name ?? "-"}</div>
        <div>Brand: {item?.brandCode ?? "-"}</div>
        <div>Type: {item?.type ?? "-"}</div>
        <div>Variant: {item?.variant ?? "-"}</div>
      </div>
    </div>
  )
}

function SelectField({
  label,
  value,
  values,
  onChange,
}: {
  label: string
  value: string
  values: string[]
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {values.map((item) => (
            <SelectItem key={item} value={item}>
              {item}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
